import { errorText } from '../../../../lib/apiErrors';
import { wikiErrorStatus } from '../../../../hooks/useWikiV2';
import useWikiRole from '../../shared/useWikiRole';
import { invalidateWiki, wikiKeys } from '../../shared/useWikiQueries';

/**
 * Shared by the three FE-brain-ops tabs (Review queue, Emotion insights,
 * Settings): the verbatim prototype toasts (E2 §1.3, §2.2, §3), the Brain
 * access props, the query keys and the API-error → toast mapping.
 *
 * Lives under review/ because FE-brain-ops owns only review/, insights/ and
 * settings/; insights and settings import it from here.
 */

/** "{what} needs the Manage AI Brain permission. Wiki admins have it." (prototype `denied`). */
export function deniedToast(what) {
    return `${what} needs the Manage AI Brain permission. Wiki admins have it.`;
}

/**
 * Every toast these tabs raise. The first eight are the prototype's own copy
 * (verbatim); `reverted` and `switchedToRouter` are not in the prototype (it
 * has no Revert in the queue and no way back from JEV) — see
 * notes/FE-brain-ops.md "Deviations". API failures toast the server's
 * `detail` sentence (`brainErrorToast`).
 */
export const BRAIN_OPS_TOASTS = Object.freeze({
    // Review queue (E2 §1.3)
    approved: "Approved. It's live now, and you can turn it off from the tree at any time.",
    rejectedRedraft: 'Rejected. The AI will draft a narrower version if the pattern keeps coming up.',
    rejected: 'Rejected. Nothing changed in the Brain.',
    // Emotion insights (E2 §2.2)
    investigateDenied: 'Opening conversations needs the Investigate conversations permission. You can see the topic totals.',
    namesShown: 'Names shown. This was added to the audit log.',
    // Settings (E2 §3)
    settingsDenied: deniedToast('Changing settings'),
    switchedToJev: 'Category routing switched to JEV. You can switch back from here at any time.',
    switchJevDenied: deniedToast('Switching to JEV'),
    // Not in the prototype (real-data affordances the contract adds).
    reverted: 'Reverted. The Brain is back to how it was before this change.',
    switchedToRouter: 'Category routing switched back to the current router.',
});

/** The five reject reasons, verbatim and in prototype order. */
export const REJECT_REASONS = Object.freeze(['Too broad', 'Duplicate', 'Wrong', 'Not needed', 'Redraft it']);

/** React Query keys for the FE-brain-ops reads (all under ['wiki','brain']). */
export const brainOpsKeys = Object.freeze({
    proposals: () => wikiKeys.brain('proposals'),
    insights: () => wikiKeys.brain('insights'),
    settings: () => wikiKeys.brain('settings'),
});

/** API paths (relative to /api), BE-brain contract (contracts/BE-brain.md). */
export const brainOpsPaths = Object.freeze({
    proposals: '/wiki/brain/proposals',
    accept: (id) => `/wiki/brain/proposals/${encodeURIComponent(id)}/accept`,
    reject: (id) => `/wiki/brain/proposals/${encodeURIComponent(id)}/reject`,
    revert: (id) => `/wiki/brain/proposals/${encodeURIComponent(id)}/revert`,
    replay: (id) => `/wiki/brain/proposals/${encodeURIComponent(id)}/replay`,
    insights: '/wiki/brain/insights',
    conversations: (topicId) => `/wiki/brain/insights/topics/${encodeURIComponent(topicId)}/conversations`,
    reveal: (topicId) => `/wiki/brain/insights/topics/${encodeURIComponent(topicId)}/conversations/reveal`,
    settings: '/wiki/brain/settings',
    routing: '/wiki/brain/settings/routing',
});

/**
 * After any Brain write: the tree (proposal nodes), the queue, the tab and
 * nav pending counts and the Home callout all read from these scopes.
 */
export function invalidateBrain(queryClient) {
    return invalidateWiki(queryClient, 'brain', 'counts', 'home');
}

/**
 * The Brain access props. BrainPage (FE-brain-tree) renders every tab with
 * `{ role, canManage, readOnly }`; `canInvestigate` may come as a prop too.
 * Anything not passed falls back to `useWikiRole()` so a tab is never more
 * permissive than the caller's real permissions. `role` may be the
 * `useWikiRole()` object or the role id string.
 */
export function useBrainAccess({ role, canManage, readOnly, canInvestigate } = {}) {
    const wikiRole = useWikiRole();
    const roleObj = role && typeof role === 'object' ? role : null;
    const manage = typeof canManage === 'boolean'
        ? canManage
        : Boolean(roleObj ? roleObj.canBrainManage : wikiRole.canBrainManage);
    const investigate = typeof canInvestigate === 'boolean'
        ? canInvestigate
        : Boolean(roleObj ? roleObj.canInvestigate : wikiRole.canInvestigate);
    return {
        canManage: manage,
        readOnly: typeof readOnly === 'boolean' ? readOnly : !manage,
        canInvestigate: investigate,
    };
}

function isPermissionMiss(error) {
    const detail = error?.response?.data?.detail;
    return wikiErrorStatus(error) === 403 && typeof detail === 'string' && /^Permission denied/i.test(detail);
}

/**
 * The toast for a failed Brain call: a bare RBAC miss becomes the prototype
 * denial for `what`; every other failure carries a sentence the contract says
 * the UI can toast verbatim (`detail`, or `detail.message` for plan gates).
 */
export function brainErrorToast(error, what) {
    if (what && isPermissionMiss(error)) return deniedToast(what);
    return errorText(error, 'Something went wrong. Please try again.');
}

/** A percentage for display: 100 → "100%", null → "—". */
export function pctLabel(value) {
    return Number.isFinite(Number(value)) && value !== null && value !== '' ? `${Math.round(Number(value))}%` : '—';
}

/** Clamp to a 0–100 bar width. */
export function barWidth(value) {
    const n = Number(value);
    if (!Number.isFinite(n) || value === null) return 0;
    return Math.max(0, Math.min(100, n));
}
